import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { citiesForState, stateFromGstin, statesForSelect } from '@kortex/shared';
import { allowance, AllowanceNotice } from '../components/PlanAllowance';
import { api, fieldErrors } from '../lib/api';
import { useAuth } from '../lib/auth';
import { dateTime } from '../lib/format';
import { PageHeader, Tabs } from '../components/Layout';
import {
  ConfirmDialog,
  Empty,
  ErrorState,
  Kpi,
  Loading,
  Modal,
  Select,
  StatusChip,
  TextInput,
  useToast,
} from '../components/ui';

interface Membership {
  propertyId: string;
  propertyName: string;
  propertyCode: string;
  role: string;
  roleLabel: string;
}

interface User {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  isActive: boolean;
  isLocked: boolean;
  mustChangePassword: boolean;
  twoFactorEnabled: boolean;
  lastLoginAt: string | null;
  memberships: Membership[];
}

interface RoleInfo {
  role: string;
  label: string;
  permissions: string[];
}

interface PropertyRow {
  id: string;
  name: string;
  code: string;
  city: string;
  pincode: string | null;
  gstin: string;
  isActive: boolean;
  rooms: number;
  users: number;
}

interface TerminalSummary {
  deviceId: string;
  deviceCode: string;
  name: string | null;
  isActive: boolean;
  registeredAt: string | null;
  appVersion: string | null;
  lastSeenAt: string;
  lastSyncAt: string | null;
}

export function UsersPage() {
  const queryClient = useQueryClient();
  const { can, session, subscription } = useAuth();
  const { push, fail } = useToast();
  const [tab, setTab] = useState<'users' | 'roles' | 'properties' | 'devices'>('users');
  const [creating, setCreating] = useState(false);
  const [addingProperty, setAddingProperty] = useState(false);
  const [editing, setEditing] = useState<User | null>(null);

  const users = useQuery({ queryKey: ['users'], queryFn: () => api.get<User[]>('/api/users') });
  const roles = useQuery({
    queryKey: ['roles'],
    queryFn: () => api.get<RoleInfo[]>('/api/users/roles'),
  });
  const properties = useQuery({
    queryKey: ['properties'],
    queryFn: () => api.get<PropertyRow[]>('/api/properties'),
  });
  // POS terminals (Phase 6 device configuration model) - registration itself
  // happens on the device (pages/DeviceSetup.tsx); this is the fleet view an
  // administrator uses to see and deactivate terminals without touching the
  // tablet in person.
  const devices = useQuery({
    queryKey: ['pos-devices'],
    queryFn: () => api.get<TerminalSummary[]>('/api/sync/devices'),
    enabled: can('property:manage'),
  });
  const deactivateDevice = useMutation({
    mutationFn: (deviceId: string) => api.post('/api/sync/devices/deactivate', { deviceId }),
    onSuccess: () => {
      push('Terminal deactivated');
      void devices.refetch();
    },
    onError: fail,
  });

  if (users.isLoading) return <Loading />;
  if (users.error) return <ErrorState error={users.error} onRetry={() => void users.refetch()} />;

  const rows = users.data ?? [];
  const propertyRows = properties.data ?? [];

  /*
   * How much of the plan is spoken for.
   *
   * Counted from the two lists already on this screen rather than from the
   * subscription summary, which is fetched once when the session loads:
   * adding a user has to move the figure at once, or the cap looks like it
   * lags a page reload behind. Counted the way the server guard counts -
   * active users, every property - so what is shown here and what the API
   * refuses cannot disagree.
   */
  const seats = subscription
    ? allowance(rows.filter((u) => u.isActive).length, subscription.limits.maxUsers)
    : null;
  const slots = subscription
    ? allowance(propertyRows.length, subscription.limits.maxProperties)
    : null;

  return (
    <div className="stack">
      <PageHeader
        title="Users & Roles"
        subtitle="Who can sign in, and what each role is allowed to do"
        actions={
          can('users:manage') ? (
            <button
              className="btn primary"
              onClick={() => setCreating(true)}
              // Refused by the server anyway; better to say so before they
              // have filled in a name, an email and a set of roles.
              disabled={seats?.full ?? false}
              title={
                seats?.full
                  ? `The ${subscription?.planName} plan covers ${seats.limit} users. Upgrade your plan to add another.`
                  : undefined
              }
            >
              + New user
            </button>
          ) : null
        }
      />

      <div className="kpis">
        <Kpi
          label="Users"
          value={rows.length}
          foot={
            seats
              ? `${seats.used} of ${seats.limit} active on the ${subscription!.planName} plan`
              : `${rows.filter((u) => u.isActive).length} active`
          }
          tone={seats?.full ? 'amber' : ''}
        />
        <Kpi
          label="Properties"
          value={propertyRows.length}
          foot={slots ? `${slots.used} of ${slots.limit} on your plan` : 'In this organisation'}
          tone={slots?.full ? 'amber' : 'blue'}
        />
        <Kpi
          label="Locked out"
          value={rows.filter((u) => u.isLocked).length}
          foot="Too many failed sign-ins"
          tone={rows.some((u) => u.isLocked) ? 'amber' : ''}
        />
        <Kpi
          label="Must change password"
          value={rows.filter((u) => u.mustChangePassword).length}
          foot="On next sign-in"
        />
      </div>

      <Tabs
        tabs={[
          { id: 'users', label: 'Users' },
          { id: 'properties', label: 'Properties' },
          can('property:manage') ? { id: 'devices', label: 'POS terminals' } : null,
          { id: 'roles', label: 'Role permissions' },
        ].filter((t): t is { id: typeof tab; label: string } => t !== null)}
        active={tab}
        onChange={setTab}
      />

      {tab === 'properties' ? (
        <div className="card">
          <div className="card-head">
            <h2>Properties</h2>
            {can('property:manage') ? (
              <button
                className="btn primary sm"
                onClick={() => setAddingProperty(true)}
                disabled={slots?.full ?? false}
              >
                + Add property
              </button>
            ) : null}
          </div>

          {slots?.full && can('property:manage') ? (
            <div className="card-body">
              <AllowanceNotice
                planName={subscription!.planName}
                what="properties"
                allowance={slots}
                canBill={can('settings:manage')}
              />
            </div>
          ) : null}

          {propertyRows.length === 0 ? (
            <Empty message="No properties yet" />
          ) : (
            <div className="table-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Property</th>
                    <th>City</th>
                    <th>GSTIN</th>
                    <th className="num">Rooms</th>
                    <th className="num">Users</th>
                  </tr>
                </thead>
                <tbody>
                  {(properties.data ?? []).map((property) => (
                    <tr key={property.id}>
                      <td>
                        <div className="b">{property.name}</div>
                        <div className="xs muted mono">{property.code}</div>
                      </td>
                      <td className="small">{property.city}</td>
                      <td className="small mono">{property.gstin}</td>
                      <td className="num">{property.rooms}</td>
                      <td className="num">{property.users}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="card-body">
            <p className="small muted">
              Each property keeps its own menu, stock, rooms and invoice numbering, and staff are
              given access to one at a time. Switch between them from the name at the top of the
              sidebar. How many you may have comes from your plan.
            </p>
          </div>
        </div>
      ) : null}

      {tab === 'devices' ? (
        <div className="card">
          <div className="card-head">
            <h2>POS terminals</h2>
          </div>

          {devices.isLoading ? (
            <Loading />
          ) : devices.error ? (
            <ErrorState error={devices.error} onRetry={() => void devices.refetch()} />
          ) : (devices.data?.length ?? 0) === 0 ? (
            <Empty message="No device has synced from here yet" />
          ) : (
            <div className="table-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Terminal</th>
                    <th>Device code</th>
                    <th>Status</th>
                    <th>Last seen</th>
                    <th>Last synced</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {(devices.data ?? []).map((device) => (
                    <tr key={device.deviceId}>
                      <td>
                        <div className="b">{device.name ?? '(not registered)'}</div>
                        <div className="xs muted mono">{device.deviceId}</div>
                      </td>
                      <td className="small mono">{device.deviceCode}</td>
                      <td>
                        {!device.registeredAt ? (
                          <span className="chip">Auto-discovered</span>
                        ) : device.isActive ? (
                          <span className="chip ok">Registered</span>
                        ) : (
                          <span className="chip danger">Deactivated</span>
                        )}
                      </td>
                      <td className="small">{dateTime(device.lastSeenAt)}</td>
                      <td className="small">
                        {device.lastSyncAt ? dateTime(device.lastSyncAt) : '—'}
                      </td>
                      <td>
                        {device.registeredAt && device.isActive ? (
                          <button
                            className="btn sm"
                            onClick={() => {
                              if (
                                window.confirm(
                                  `Deactivate "${device.name}"? Its next sign-in will need to be re-registered before it can operate.`,
                                )
                              ) {
                                deactivateDevice.mutate(device.deviceId);
                              }
                            }}
                            disabled={deactivateDevice.isPending}
                          >
                            Deactivate
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="card-body">
            <p className="small muted">
              A terminal is registered from the tablet itself the first time it signs in
              (docs/android/PHASE-6-BASELINE-AUDIT.md). Once registered, it always opens here —
              deactivating it lets a lost or reassigned tablet be registered somewhere else.
              "Auto-discovered" means a device has synced from here without ever being confirmed by
              an administrator, so it is not pinned to anything yet.
            </p>
          </div>
        </div>
      ) : null}

      {tab === 'users' ? (
        <div className="card">
          {/* Said once, here, in the same words the API refuses with. */}
          {seats?.full && can('users:manage') ? (
            <div className="card-body">
              <AllowanceNotice
                planName={subscription!.planName}
                what="users"
                allowance={seats}
                canBill={can('settings:manage')}
              />
            </div>
          ) : null}
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Access</th>
                  <th>Last signed in</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div className="b">{user.name}</div>
                      <div className="xs muted">{user.email}</div>
                    </td>
                    <td>
                      <div className="row wrap gap-4">
                        {user.memberships.map((membership) => (
                          <span className="chip" key={membership.propertyId}>
                            {membership.propertyCode} · {membership.roleLabel}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="small">
                      {user.lastLoginAt ? (
                        dateTime(user.lastLoginAt)
                      ) : (
                        <span className="muted">Never</span>
                      )}
                    </td>
                    <td>
                      <div className="row gap-4">
                        <StatusChip status={user.isActive ? 'ACTIVE' : 'INACTIVE'} />
                        {user.isLocked ? <span className="chip danger">Locked</span> : null}
                      </div>
                    </td>
                    <td className="right">
                      {can('users:manage') && user.id !== session?.user.id ? (
                        <button className="btn ghost sm" onClick={() => setEditing(user)}>
                          Manage
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : tab === 'roles' ? (
        <div className="card">
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th style={{ width: 160 }}>Role</th>
                  <th className="num" style={{ width: 90 }}>
                    Permissions
                  </th>
                  <th>Allowed to</th>
                </tr>
              </thead>
              <tbody>
                {(roles.data ?? []).map((role) => (
                  <tr key={role.role}>
                    <td className="b">{role.label}</td>
                    <td className="num">{role.permissions.length}</td>
                    <td>
                      <div className="row wrap gap-4">
                        {role.permissions.map((permission) => (
                          <span
                            className="chip"
                            key={permission}
                            style={{ fontFamily: 'var(--mono)', fontSize: 10.5 }}
                          >
                            {permission}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {addingProperty ? (
        <PropertyModal
          onClose={() => setAddingProperty(false)}
          onDone={() => {
            setAddingProperty(false);
            void properties.refetch();
          }}
        />
      ) : null}

      {creating || editing ? (
        <UserModal
          user={editing}
          properties={properties.data ?? []}
          roles={roles.data ?? []}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onDone={() => {
            setCreating(false);
            setEditing(null);
            void queryClient.invalidateQueries({ queryKey: ['users'] });
          }}
        />
      ) : null}
    </div>
  );
}

function UserModal({
  user,
  properties,
  roles,
  onClose,
  onDone,
}: {
  user: User | null;
  properties: PropertyRow[];
  roles: RoleInfo[];
  onClose: () => void;
  onDone: () => void;
}) {
  const { fail, push } = useToast();
  const { session } = useAuth();
  const [name, setName] = useState(user?.name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [password, setPassword] = useState('');
  const [isActive, setIsActive] = useState(user?.isActive ?? true);
  const [confirmingTwoFactorReset, setConfirmingTwoFactorReset] = useState(false);
  const [memberships, setMemberships] = useState<Array<{ propertyId: string; role: string }>>(
    user?.memberships.map((m) => ({ propertyId: m.propertyId, role: m.role })) ?? [
      { propertyId: properties[0]?.id ?? '', role: 'CASHIER' },
    ],
  );

  const save = useMutation({
    mutationFn: () =>
      user
        ? api.patch(`/api/users/${user.id}`, { name, isActive, memberships })
        : api.post('/api/users', { name, email, password, memberships }),
    onSuccess: () => {
      push(user ? 'User updated' : 'User created', 'ok');
      onDone();
    },
    onError: fail,
  });

  const resetPassword = useMutation({
    mutationFn: () =>
      api.post('/api/users/reset-password', { userId: user!.id, newPassword: password }),
    onSuccess: () => {
      push('Password reset — the user must change it at next sign-in', 'ok');
      setPassword('');
    },
    onError: fail,
  });

  const resetTwoFactor = useMutation({
    mutationFn: () => api.post(`/api/users/${user!.id}/reset-2fa`),
    onSuccess: () => {
      push('Two-step verification reset for this account', 'ok');
      setConfirmingTwoFactorReset(false);
      onDone();
    },
    onError: (error) => {
      setConfirmingTwoFactorReset(false);
      fail(error);
    },
  });

  const isOwnRow = user && session?.user.id === user.id;

  return (
    <Modal
      title={user ? `Manage ${user.name}` : 'New user'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          {user && user.twoFactorEnabled && !isOwnRow ? (
            <button
              className="btn"
              disabled={resetTwoFactor.isPending}
              onClick={() => setConfirmingTwoFactorReset(true)}
            >
              Reset 2FA
            </button>
          ) : null}
          {user && password ? (
            <button
              className="btn"
              disabled={resetPassword.isPending}
              onClick={() => resetPassword.mutate()}
            >
              Reset password
            </button>
          ) : null}
          <button
            className="btn primary"
            disabled={
              !name.trim() || (!user && (!email.trim() || password.length < 12)) || save.isPending
            }
            onClick={() => save.mutate()}
          >
            {user ? 'Save changes' : 'Create user'}
          </button>
        </>
      }
    >
      <div className="col gap-16">
        <div className="form-grid">
          <TextInput
            className="span2"
            label="Full name *"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          {!user ? (
            <>
              <TextInput
                className="span2"
                label="Email *"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <TextInput
                className="span2"
                label="Temporary password *"
                type="text"
                value={password}
                hint="At least 12 characters with upper case, lower case and a number. The user must change it at first sign-in."
                onChange={(e) => setPassword(e.target.value)}
              />
            </>
          ) : (
            <TextInput
              className="span2"
              label="New password (optional)"
              type="text"
              value={password}
              hint="Fill this in and press Reset password to force a new one"
              onChange={(e) => setPassword(e.target.value)}
            />
          )}
        </div>

        <div className="card">
          <div className="card-head">
            <h2>Property access</h2>
            <button
              className="btn sm"
              onClick={() =>
                setMemberships([
                  ...memberships,
                  { propertyId: properties[0]?.id ?? '', role: 'CASHIER' },
                ])
              }
            >
              + Grant access
            </button>
          </div>
          <div className="card-body col gap-6">
            {memberships.map((membership, index) => (
              <div className="row gap-6" key={index}>
                <select
                  className="select"
                  style={{ flex: 2 }}
                  value={membership.propertyId}
                  onChange={(e) => {
                    const next = [...memberships];
                    next[index] = { ...membership, propertyId: e.target.value };
                    setMemberships(next);
                  }}
                >
                  {properties.map((property) => (
                    <option key={property.id} value={property.id}>
                      {property.name} ({property.code})
                    </option>
                  ))}
                </select>
                <select
                  className="select"
                  style={{ flex: 1 }}
                  value={membership.role}
                  onChange={(e) => {
                    const next = [...memberships];
                    next[index] = { ...membership, role: e.target.value };
                    setMemberships(next);
                  }}
                >
                  {roles.map((role) => (
                    <option key={role.role} value={role.role}>
                      {role.label}
                    </option>
                  ))}
                </select>
                <button
                  className="btn ghost icon sm"
                  disabled={memberships.length === 1}
                  onClick={() => setMemberships(memberships.filter((_, i) => i !== index))}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>

        {user ? (
          <label className="checkbox">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            Account is active (turning this off signs the user out everywhere)
          </label>
        ) : null}
      </div>

      {confirmingTwoFactorReset ? (
        <ConfirmDialog
          title="Reset two-step verification?"
          message={`${user?.name} will need to set up two-step verification again from scratch - their current recovery codes stop working immediately.`}
          confirmLabel="Reset 2FA"
          danger
          busy={resetTwoFactor.isPending}
          onConfirm={() => resetTwoFactor.mutate()}
          onCancel={() => setConfirmingTwoFactorReset(false)}
        />
      ) : null}
    </Modal>
  );
}

/* ------------------------------------------------------------------ */
/* Settings                                                            */
/* ------------------------------------------------------------------ */

/** The GST slabs an Indian supply can legally sit in. */
const GST_SLABS = [0, 5, 12, 18, 28] as const;

interface RoomTypeRow {
  id: string;
  name: string;
  baseTariff: number;
}

interface PropertyProfile {
  id: string;
  name: string;
  code: string;
  legalName: string | null;
  gstin: string;
  stateCode: string;
  address: string;
  city: string;
  pincode: string | null;
  phone: string;
  email: string | null;
  fssai: string | null;
  currency: string;
  timezone: string;
  settings: {
    targetFoodCostPct: number;
    serviceChargePct: number;
    invoicePrefix: string;
    security: {
      idleTimeoutMinutes: number;
      offlineRetentionDays?: number;
      offlineLockDays?: number;
      requireDeviceRegistration?: boolean;
    };
    charges: { packagingTakeaway: number; packagingDelivery: number; deliveryFee: number };
    tax: {
      restaurantSac: string;
      accommodationSac: string;
      roomTariffThreshold: number;
      roomGstBelow: number;
      roomGstAbove: number;
    };
    payroll: {
      basicPct: number;
      hraPct: number;
      pfPct: number;
      pfCeiling: number;
      esiPct: number;
      esiCeiling: number;
      ptAmount: number;
      ptThreshold: number;
      otMultiplier: number;
      advanceRecoveryCapPct: number;
      standardHoursPerDay: number;
    };
  };
}

/**
 * A second hotel under the same account.
 *
 * The API has always taken this; there was simply nowhere to ask for it, so an
 * owner on a multi-property plan had one property and no way to add another.
 * The fields are the ones a tax invoice needs, because that is what a property
 * is for - the plan limit is enforced on the server and comes back as a plain
 * refusal rather than a disabled button that explains nothing.
 */
function PropertyModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { fail, push } = useToast();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [form, setForm] = useState({
    name: '',
    code: '',
    legalName: '',
    gstin: '',
    stateCode: '',
    address: '',
    city: '',
    pincode: '',
    phone: '',
    email: '',
    fssai: '',
    currency: 'INR',
    timezone: 'Asia/Kolkata',
  });

  const set = (patch: Partial<typeof form>) => {
    setForm({ ...form, ...patch });
    setErrors((prev) => {
      const next = { ...prev };
      for (const key of Object.keys(patch)) delete next[key];
      return next;
    });
  };

  const create = useMutation({
    mutationFn: () =>
      // Blank optional fields are left out rather than sent as empty strings:
      // "a name of at least one character, or nothing" does not include "".
      api.post(
        '/api/properties',
        Object.fromEntries(Object.entries(form).filter(([, value]) => String(value).trim() !== '')),
      ),
    onSuccess: () => {
      push(`${form.name} added`, 'ok');
      onDone();
    },
    onError: (caught) => {
      setErrors(fieldErrors(caught));
      fail(caught);
    },
  });

  const states = statesForSelect();
  const cities = citiesForState(form.stateCode);

  return (
    <Modal
      title="New property"
      size="wide"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={!form.name.trim() || !form.code.trim() || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? 'Adding…' : 'Add property'}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <TextInput
          label="Property name *"
          autoFocus
          placeholder="Kortex Riverside"
          value={form.name}
          error={errors['name']}
          onChange={(e) => {
            const name = e.target.value;
            // A short code nobody has to invent, but can still overwrite.
            const suggested = name
              .toUpperCase()
              .replace(/[^A-Z0-9]/g, '')
              .slice(0, 6);
            set({ name, ...(form.code ? {} : { code: suggested }) });
          }}
        />
        <TextInput
          label="Short code *"
          hint="On document numbers. 2-12 letters or digits"
          placeholder="KRP"
          value={form.code}
          error={errors['code']}
          onChange={(e) => set({ code: e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '') })}
        />
        <TextInput
          className="span2"
          label="Registered legal name"
          placeholder="Kortex Hospitality Pvt Ltd"
          value={form.legalName}
          error={errors['legalName']}
          onChange={(e) => set({ legalName: e.target.value })}
        />
        <TextInput
          label="GSTIN *"
          placeholder="29AABCK1234M1Z5"
          value={form.gstin}
          error={errors['gstin']}
          onChange={(e) => {
            const gstin = e.target.value.toUpperCase();
            const derived = stateFromGstin(gstin);
            set({ gstin, ...(derived && !form.stateCode ? { stateCode: derived.code } : {}) });
          }}
        />
        <Select
          label="State *"
          value={form.stateCode}
          error={errors['stateCode']}
          onChange={(e) => set({ stateCode: e.target.value, city: '' })}
        >
          <option value="">Choose a state…</option>
          {states.map((state) => (
            <option key={state.code} value={state.code}>
              {state.name} ({state.code})
            </option>
          ))}
        </Select>
        <TextInput
          className="span2"
          label="Address *"
          placeholder="9 Koregaon Park"
          value={form.address}
          error={errors['address']}
          onChange={(e) => set({ address: e.target.value })}
        />
        {cities.length > 0 ? (
          <Select
            label="City *"
            value={form.city}
            error={errors['city']}
            onChange={(e) => set({ city: e.target.value })}
          >
            <option value="">Choose a city…</option>
            {cities.map((city) => (
              <option key={city} value={city}>
                {city}
              </option>
            ))}
          </Select>
        ) : (
          <TextInput
            label="City *"
            value={form.city}
            error={errors['city']}
            onChange={(e) => set({ city: e.target.value })}
          />
        )}
        <TextInput
          label="PIN code *"
          inputMode="numeric"
          placeholder="411001"
          value={form.pincode}
          error={errors['pincode']}
          onChange={(e) => set({ pincode: e.target.value.replace(/\D/g, '').slice(0, 6) })}
        />
        <TextInput
          label="Phone *"
          placeholder="+91 20 4000 5678"
          value={form.phone}
          error={errors['phone']}
          onChange={(e) => set({ phone: e.target.value })}
        />
        <TextInput
          label="Email *"
          type="email"
          placeholder="hello@riverside.in"
          value={form.email}
          error={errors['email']}
          onChange={(e) => set({ email: e.target.value })}
        />
        <TextInput
          label="FSSAI licence"
          placeholder="12345678901234"
          value={form.fssai}
          error={errors['fssai']}
          onChange={(e) => set({ fssai: e.target.value.replace(/\D/g, '').slice(0, 14) })}
        />
      </div>

      <p className="small muted" style={{ marginTop: 14 }}>
        You are added as its owner, and it starts empty — its own menu, stock, rooms and invoice
        numbering. Switch to it from the property name at the top of the sidebar.
      </p>
    </Modal>
  );
}

export function SettingsPage() {
  const queryClient = useQueryClient();
  const { fail, push } = useToast();
  const { can } = useAuth();
  const property = useQuery({
    queryKey: ['property'],
    queryFn: () => api.get<PropertyProfile>('/api/property'),
  });
  const roomTypes = useQuery({
    queryKey: ['room-types'],
    queryFn: () => api.get<RoomTypeRow[]>('/api/room-types'),
  });

  const [profile, setProfile] = useState<Partial<PropertyProfile> | null>(null);
  const [payroll, setPayroll] = useState<PropertyProfile['settings'] | null>(null);

  const saveProfile = useMutation({
    mutationFn: (input: Partial<PropertyProfile>) => api.patch('/api/property', input),
    onSuccess: () => {
      push('Business profile saved', 'ok');
      setProfile(null);
      void queryClient.invalidateQueries({ queryKey: ['property'] });
    },
    onError: fail,
  });

  const saveSettings = useMutation({
    mutationFn: (input: unknown) => api.patch('/api/property/settings', input),
    onSuccess: () => {
      push('Settings updated', 'ok');
      setPayroll(null);
      void queryClient.invalidateQueries({ queryKey: ['property'] });
      void queryClient.invalidateQueries({ queryKey: ['menu'] });
    },
    onError: fail,
  });

  if (property.isLoading) return <Loading />;
  if (property.error)
    return <ErrorState error={property.error} onRetry={() => void property.refetch()} />;

  const data = property.data!;
  const form = { ...data, ...(profile ?? {}) };
  const rates = payroll ?? data.settings;
  const readOnly = !can('settings:manage');

  // Which rooms each slab actually lands on, read from the tariffs this hotel
  // has set rather than from a list written once and never true again.
  const threshold = rates.tax.roomTariffThreshold;
  const names = (below: boolean) =>
    (roomTypes.data ?? [])
      .filter((t) => (below ? t.baseTariff <= threshold : t.baseTariff > threshold))
      .map((t) => t.name)
      .join(', ');
  const roomTypesAtOrBelow = names(true);
  const roomTypesAbove = names(false);

  return (
    <div className="stack" style={{ maxWidth: 1000 }}>
      <PageHeader title="Settings" subtitle="Business identity, tax profile and payroll rules" />

      {readOnly ? (
        <div className="readonly-note">Your role can view these settings but not change them.</div>
      ) : null}

      <div className="card">
        <div className="card-head">
          <h2>Business profile</h2>
          <span className="chip">Printed on every invoice</span>
        </div>
        <div className="card-body">
          <div className="form-grid">
            <TextInput
              className="span2"
              label="Property name"
              value={form.name ?? ''}
              disabled={readOnly}
              onChange={(e) => setProfile({ ...profile, name: e.target.value })}
            />
            <TextInput
              className="span2"
              label="Legal name"
              value={form.legalName ?? ''}
              disabled={readOnly}
              onChange={(e) => setProfile({ ...profile, legalName: e.target.value })}
            />
            <TextInput
              className="span2"
              label="Address"
              value={form.address ?? ''}
              disabled={readOnly}
              onChange={(e) => setProfile({ ...profile, address: e.target.value })}
            />
            <TextInput
              label="City"
              value={form.city ?? ''}
              disabled={readOnly}
              onChange={(e) => setProfile({ ...profile, city: e.target.value })}
            />
            <TextInput
              label="PIN code"
              inputMode="numeric"
              placeholder="560001"
              hint="Required on an e-invoice"
              value={form.pincode ?? ''}
              disabled={readOnly}
              onChange={(e) =>
                setProfile({ ...profile, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) })
              }
            />
            <TextInput
              label="GSTIN"
              value={form.gstin ?? ''}
              disabled={readOnly}
              onChange={(e) => setProfile({ ...profile, gstin: e.target.value.toUpperCase() })}
            />
            <TextInput
              label="GST state code"
              value={form.stateCode ?? ''}
              disabled={readOnly}
              hint="Decides CGST+SGST versus IGST"
              onChange={(e) => setProfile({ ...profile, stateCode: e.target.value })}
            />
            <TextInput
              label="Phone"
              value={form.phone ?? ''}
              disabled={readOnly}
              onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
            />
            <TextInput
              label="FSSAI licence"
              value={form.fssai ?? ''}
              disabled={readOnly}
              onChange={(e) => setProfile({ ...profile, fssai: e.target.value })}
            />
          </div>
          {!readOnly ? (
            <button
              className="btn primary"
              style={{ marginTop: 14 }}
              disabled={!profile || saveProfile.isPending}
              onClick={() => saveProfile.mutate(profile!)}
            >
              Save profile
            </button>
          ) : null}
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Charges on a bill</h2>
          <span className="chip">Added automatically by ticket type</span>
        </div>
        <div className="card-body">
          <div className="form-grid three">
            <TextInput
              label="Packing, takeaway (₹)"
              type="number"
              min={0}
              step={5}
              disabled={readOnly}
              hint="Per takeaway order. 0 charges nothing."
              value={rates.charges.packagingTakeaway}
              onChange={(e) =>
                setPayroll({
                  ...rates,
                  charges: {
                    ...rates.charges,
                    packagingTakeaway: Math.max(0, Number(e.target.value) || 0),
                  },
                })
              }
            />
            <TextInput
              label="Packing, delivery (₹)"
              type="number"
              min={0}
              step={5}
              disabled={readOnly}
              hint="Per delivery order."
              value={rates.charges.packagingDelivery}
              onChange={(e) =>
                setPayroll({
                  ...rates,
                  charges: {
                    ...rates.charges,
                    packagingDelivery: Math.max(0, Number(e.target.value) || 0),
                  },
                })
              }
            />
            <TextInput
              label="Delivery charge (₹)"
              type="number"
              min={0}
              step={5}
              disabled={readOnly}
              hint="Per delivery order, on top of packing."
              value={rates.charges.deliveryFee}
              onChange={(e) =>
                setPayroll({
                  ...rates,
                  charges: {
                    ...rates.charges,
                    deliveryFee: Math.max(0, Number(e.target.value) || 0),
                  },
                })
              }
            />
          </div>
          <p className="small muted" style={{ marginTop: 10 }}>
            Charged on the ticket type they belong to, taxed at the same GST rate as the food, and
            added after any bill discount — a discount is on the meal, not on the box it leaves in.
            A charge left at 0 puts no line on the bill at all.
          </p>
          <p className="small muted" style={{ marginTop: 8 }}>
            <b>Service charge</b> is set under Costing &amp; payroll rules and applies to tables and
            room service. It is 0 by default and worth leaving there unless you have decided
            otherwise: Indian consumer guidance is that a service charge must not be added to a food
            bill automatically, and a guest may ask for it to be removed.
          </p>
          {!readOnly ? (
            <button
              className="btn primary"
              style={{ marginTop: 14 }}
              disabled={!payroll || saveSettings.isPending}
              onClick={() => saveSettings.mutate({ charges: rates.charges })}
            >
              Save charges
            </button>
          ) : null}
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Sign-in &amp; sessions</h2>
          <span className="chip">Applies to everybody at this property</span>
        </div>
        <div className="card-body">
          <div className="form-grid three">
            <TextInput
              label="Sign out after idle (minutes)"
              type="number"
              min={0}
              max={1440}
              step={5}
              disabled={readOnly}
              hint="0 leaves people signed in until they sign out. A terminal on the front desk is worth a limit."
              value={rates.security.idleTimeoutMinutes}
              onChange={(e) =>
                setPayroll({
                  ...rates,
                  security: {
                    ...rates.security,
                    idleTimeoutMinutes: Math.max(0, Math.min(1440, Number(e.target.value) || 0)),
                  },
                })
              }
            />
            <TextInput
              label="Keep synced bills on devices (days)"
              type="number"
              min={1}
              max={90}
              disabled={readOnly}
              hint="How long an offline device keeps bills the server already has. Shorter means less on a lost tablet."
              value={rates.security.offlineRetentionDays ?? 7}
              onChange={(e) =>
                setPayroll({
                  ...rates,
                  security: {
                    ...rates.security,
                    offlineRetentionDays: Math.max(1, Math.min(90, Number(e.target.value) || 1)),
                  },
                })
              }
            />
            <TextInput
              label="Lock a device after (days without reaching the server)"
              type="number"
              min={1}
              max={30}
              disabled={readOnly}
              hint="Nobody can sign in offline on a device that has been out of touch this long. It locks; its bills are kept and sent once it is back online."
              value={rates.security.offlineLockDays ?? 7}
              onChange={(e) =>
                setPayroll({
                  ...rates,
                  security: {
                    ...rates.security,
                    offlineLockDays: Math.max(1, Math.min(30, Number(e.target.value) || 1)),
                  },
                })
              }
            />
          </div>
          <p className="small muted" style={{ marginTop: 10 }}>
            Counted from the last thing somebody actually did, not from when they signed in — a
            cashier working a queue is never interrupted. Anyone signed out this way simply signs in
            again.{' '}
            {rates.security.idleTimeoutMinutes > 0
              ? `Currently signing out after ${rates.security.idleTimeoutMinutes} minutes of inactivity.`
              : 'Currently switched off.'}
          </p>
          <label className="checkbox" style={{ marginTop: 18 }}>
            <input
              type="checkbox"
              disabled={readOnly}
              checked={rates.security.requireDeviceRegistration ?? true}
              onChange={(e) =>
                setPayroll({
                  ...rates,
                  security: { ...rates.security, requireDeviceRegistration: e.target.checked },
                })
              }
            />
            <span>
              <b>Staff must register their Android device</b>
              <span className="small muted" style={{ display: 'block' }}>
                {(rates.security.requireDeviceRegistration ?? true)
                  ? 'On: a member of staff signing in on the Android app sees a setup screen until you register the device, and registered devices work offline.'
                  : 'Off: staff can sign in on the Android app with their email and password, like the website, with no registration. Without registration the app needs a connection - only a device you register keeps an offline copy of the till.'}{' '}
                You are never asked to register: sign in on any device, and register one from your
                account menu if you want it to work offline.
              </span>
            </span>
          </label>
          {!readOnly ? (
            <button
              className="btn primary"
              style={{ marginTop: 14 }}
              disabled={!payroll || saveSettings.isPending}
              onClick={() => saveSettings.mutate({ security: rates.security })}
            >
              Save
            </button>
          ) : null}
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Costing &amp; payroll rules</h2>
          <span className="chip warn">Applies to the next preview and repricing</span>
        </div>
        <div className="card-body">
          <div className="form-grid three">
            <TextInput
              label="Target food cost %"
              type="number"
              disabled={readOnly}
              value={rates.targetFoodCostPct}
              onChange={(e) =>
                setPayroll({ ...rates, targetFoodCostPct: Number(e.target.value) || 0 })
              }
            />
            <TextInput
              label="Service charge %"
              type="number"
              disabled={readOnly}
              value={rates.serviceChargePct}
              onChange={(e) =>
                setPayroll({ ...rates, serviceChargePct: Number(e.target.value) || 0 })
              }
            />
            <TextInput
              label="Invoice prefix"
              disabled={readOnly}
              value={rates.invoicePrefix}
              onChange={(e) => setPayroll({ ...rates, invoicePrefix: e.target.value })}
            />
            {(
              [
                ['basicPct', 'Basic % of gross'],
                ['hraPct', 'HRA % of basic'],
                ['otMultiplier', 'Overtime multiplier'],
                ['pfPct', 'PF %'],
                ['pfCeiling', 'PF wage ceiling (₹)'],
                ['esiPct', 'ESI %'],
                ['esiCeiling', 'ESI gross ceiling (₹)'],
                ['ptAmount', 'Professional tax (₹)'],
                ['ptThreshold', 'PT applies above (₹)'],
                ['advanceRecoveryCapPct', 'Advance recovery cap %'],
                ['standardHoursPerDay', 'Standard hours per day'],
              ] as const
            ).map(([key, label]) => (
              <TextInput
                key={key}
                label={label}
                type="number"
                step="0.01"
                disabled={readOnly}
                value={rates.payroll[key]}
                onChange={(e) =>
                  setPayroll({
                    ...rates,
                    payroll: { ...rates.payroll, [key]: Number(e.target.value) || 0 },
                  })
                }
              />
            ))}
          </div>
          {!readOnly ? (
            <button
              className="btn primary"
              style={{ marginTop: 14 }}
              disabled={!payroll || saveSettings.isPending}
              onClick={() =>
                saveSettings.mutate({
                  targetFoodCostPct: rates.targetFoodCostPct,
                  serviceChargePct: rates.serviceChargePct,
                  invoicePrefix: rates.invoicePrefix,
                  payroll: rates.payroll,
                })
              }
            >
              Save rules
            </button>
          ) : null}
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Tax reference</h2>
          <span className="chip warn">Applies to the next bill</span>
        </div>
        <div className="card-body">
          <p className="small muted" style={{ marginTop: 0, marginBottom: 12 }}>
            The slabs and service codes this hotel bills at. They start as the rates in the GST
            notification, and are here because a council revision should not need a new release —
            change them the day one lands, not the week after.
          </p>
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Supply</th>
                  <th>HSN / SAC</th>
                  <th className="num">GST</th>
                  <th>Applied on</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Restaurant service</td>
                  <td>
                    <TextInput
                      label=""
                      className="mono"
                      disabled={readOnly}
                      value={rates.tax.restaurantSac}
                      onChange={(e) =>
                        setPayroll({
                          ...rates,
                          tax: { ...rates.tax, restaurantSac: e.target.value },
                        })
                      }
                    />
                  </td>
                  <td className="num">Per item</td>
                  <td>
                    Every F&amp;B ticket, at the rate set on each menu item under Menu &amp; pricing
                  </td>
                </tr>
                <tr>
                  <td>
                    Accommodation, tariff ≤ ₹{rates.tax.roomTariffThreshold.toLocaleString('en-IN')}
                  </td>
                  <td rowSpan={2}>
                    <TextInput
                      label=""
                      className="mono"
                      disabled={readOnly}
                      value={rates.tax.accommodationSac}
                      onChange={(e) =>
                        setPayroll({
                          ...rates,
                          tax: { ...rates.tax, accommodationSac: e.target.value },
                        })
                      }
                    />
                  </td>
                  <td className="num">
                    <Select
                      label=""
                      disabled={readOnly}
                      value={String(rates.tax.roomGstBelow)}
                      onChange={(e) =>
                        setPayroll({
                          ...rates,
                          tax: { ...rates.tax, roomGstBelow: Number(e.target.value) },
                        })
                      }
                    >
                      {GST_SLABS.map((slab) => (
                        <option key={slab} value={slab}>
                          {slab}%
                        </option>
                      ))}
                    </Select>
                  </td>
                  <td>{roomTypesAtOrBelow || 'No room type at or below this tariff'}</td>
                </tr>
                <tr>
                  <td>
                    Accommodation, tariff &gt; ₹
                    {rates.tax.roomTariffThreshold.toLocaleString('en-IN')}
                  </td>
                  <td className="num">
                    <Select
                      label=""
                      disabled={readOnly}
                      value={String(rates.tax.roomGstAbove)}
                      onChange={(e) =>
                        setPayroll({
                          ...rates,
                          tax: { ...rates.tax, roomGstAbove: Number(e.target.value) },
                        })
                      }
                    >
                      {GST_SLABS.map((slab) => (
                        <option key={slab} value={slab}>
                          {slab}%
                        </option>
                      ))}
                    </Select>
                  </td>
                  <td>{roomTypesAbove || 'No room type above this tariff'}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="form-grid three" style={{ marginTop: 14 }}>
            <TextInput
              label="Accommodation slab changes above (₹)"
              type="number"
              min={0}
              step={100}
              disabled={readOnly}
              hint="Declared tariff per room per night. At or below this is the lower slab."
              value={rates.tax.roomTariffThreshold}
              onChange={(e) =>
                setPayroll({
                  ...rates,
                  tax: { ...rates.tax, roomTariffThreshold: Number(e.target.value) || 0 },
                })
              }
            />
          </div>

          <p className="small muted" style={{ marginTop: 10 }}>
            A buyer GSTIN registered in another state switches that invoice to IGST automatically;
            everything else is billed as CGST + SGST for state {data.stateCode}. Changing a rate
            here affects bills raised from now on — invoices already issued keep the rate they were
            filed at.
          </p>
          {!readOnly ? (
            <button
              className="btn primary"
              style={{ marginTop: 14 }}
              disabled={!payroll || saveSettings.isPending}
              onClick={() => saveSettings.mutate({ tax: rates.tax })}
            >
              Save tax rules
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Audit log                                                           */
/* ------------------------------------------------------------------ */

interface AuditEntry {
  id: string;
  action: string;
  entity: string;
  entityId: string | null;
  summary: string;
  user: string;
  email: string | null;
  ip: string | null;
  createdAt: string;
  category?: string;
  success?: boolean;
  /** Who the entry is about, by their current name (resolved when read). */
  subject?: string | null;
}

const AUDIT_CATEGORIES: Array<[string, string]> = [
  ['AUTH', 'Sign-in'],
  ['SECURITY', 'Security'],
  ['PRIVACY', 'Privacy'],
  ['EXPORT', 'Exports'],
  ['READ', 'Sensitive views'],
  ['CREATE', 'Created'],
  ['UPDATE', 'Changed'],
  ['DELETE', 'Deleted'],
  ['ADMIN', 'Administration'],
];

export function AuditPage() {
  const [offset, setOffset] = useState(0);
  const [entity, setEntity] = useState('');
  const [category, setCategory] = useState('');
  const limit = 50;

  const audit = useQuery({
    queryKey: ['audit', offset, entity, category],
    queryFn: () =>
      api.get<{ total: number; entries: AuditEntry[] }>(
        `/api/audit?limit=${limit}&offset=${offset}${entity ? `&entity=${entity}` : ''}${category ? `&category=${category}` : ''}`,
      ),
  });

  if (audit.isLoading) return <Loading />;
  if (audit.error) return <ErrorState error={audit.error} onRetry={() => void audit.refetch()} />;

  const data = audit.data!;

  return (
    <div className="stack">
      <PageHeader
        title="Audit Log"
        subtitle="Every privileged action, who took it and when"
        actions={
          <div className="row gap-6">
          <Select
            label=""
            value={category}
            onChange={(e) => {
              setCategory(e.target.value);
              setOffset(0);
            }}
          >
            <option value="">All kinds</option>
            {AUDIT_CATEGORIES.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
          <Select
            label=""
            value={entity}
            onChange={(e) => {
              setEntity(e.target.value);
              setOffset(0);
            }}
          >
            <option value="">All entities</option>
            {[
              'Invoice',
              'Order',
              'MenuItem',
              'Ingredient',
              'PurchaseOrder',
              'GoodsReceipt',
              'Stay',
              'PayrollRun',
              'User',
              'Property',
            ].map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Select>
          </div>
        }
      />

      <div className="card">
        {data.entries.length === 0 ? (
          <Empty icon="≡" message="Nothing recorded yet" />
        ) : (
          <div className="timeline">
            {data.entries.map((entry) => (
              <div className="entry" key={entry.id}>
                <div className="when">{dateTime(entry.createdAt)}</div>
                <div>
                  <div className="what">
                    {entry.success === false ? <span className="chip warn" style={{ marginRight: 6 }}>Refused</span> : null}
                    {entry.summary}
                    {entry.subject ? <span className="muted"> — {entry.subject}</span> : null}
                  </div>
                  <div className="who">
                    {entry.user} · <span className="mono">{entry.action}</span>
                    {entry.ip ? ` · ${entry.ip}` : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="card-body row between">
          <span className="small muted">
            {offset + 1}–{Math.min(offset + limit, data.total)} of {data.total}
          </span>
          <div className="row gap-6">
            <button
              className="btn sm"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - limit))}
            >
              Previous
            </button>
            <button
              className="btn sm"
              disabled={offset + limit >= data.total}
              onClick={() => setOffset(offset + limit)}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
